import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ─── Interfaces públicas ────────────────────────────────────────────────────

export interface PdfOptions {
  title: string;
  subtitle?: string;
  clientInfo?: { name?: string; email?: string; phone?: string };
  note?: string;
}

export interface PdfTableColumn {
  header: string;
  width?: number;
  align?: "left" | "center" | "right";
}

export interface CorporatePdf {
  doc: jsPDF;
  contentY: number;
}

// ─── Identidad de marca ─────────────────────────────────────────────────────

const BRAND_NAME     = "Outlet Innovaria S.L.";
const BRAND_SUBTITLE = "Outlet de Segunda Mano";
const C_PRIMARY: [number, number, number] = [0, 188, 101];   // #00BC65
const C_DARK:    [number, number, number] = [0, 133, 74];    // #00854A
const C_LIGHT:   [number, number, number] = [193, 239, 218]; // #C1EFDA
const C_GRAY:    [number, number, number] = [100, 100, 100];
const C_TEXT:    [number, number, number] = [40, 40, 40];
const LOGO_URL   = (process.env.BASE_URL || "/") + "logo_cliente.png";

// ─── Carga de logo con caché lazy ───────────────────────────────────────────

let _logoCache: string | null | undefined = undefined;

function _loadLogo(): Promise<string | null> {
  return new Promise(function(resolve) {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = function() {
        try {
          const canvas = document.createElement("canvas");
          canvas.width  = img.naturalWidth  || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext("2d");
          if (!ctx) { resolve(null); return; }
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = function() { resolve(null); };
      img.src = LOGO_URL;
    } catch (e) {
      resolve(null);
    }
  });
}

function _getLogo(): Promise<string | null> {
  if (_logoCache !== undefined) {
    return Promise.resolve(_logoCache);
  }
  return _loadLogo().then(function(logo) {
    _logoCache = logo;
    return logo;
  });
}

// ─── createDoc ──────────────────────────────────────────────────────────────

async function createDoc(opts: PdfOptions): Promise<CorporatePdf> {
  const doc   = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  let logo: string | null = null;
  try {
    logo = await _getLogo();
  } catch (e) {
    logo = null;
  }

  // Banda de cabecera verde
  doc.setFillColor(C_PRIMARY[0], C_PRIMARY[1], C_PRIMARY[2]);
  doc.rect(0, 0, pageW, 28, "F");

  // Logo (si cargó correctamente)
  if (logo) {
    try {
      doc.addImage(logo, "PNG", 6, 3, 22, 22);
    } catch (e) {
      // continuar sin logo si falla
    }
  }

  // Título del documento en blanco sobre la banda verde
  const titleX = logo ? 33 : 10;
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(opts.title, titleX, 13);

  if (opts.subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(opts.subtitle, titleX, 21);
  }

  // Nombre de empresa alineado a la derecha (blanco)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(BRAND_NAME, pageW - 8, 10, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text(BRAND_SUBTITLE, pageW - 8, 17, { align: "right" });

  let contentY = 34;

  // Fecha del día (gris, derecha)
  doc.setTextColor(C_GRAY[0], C_GRAY[1], C_GRAY[2]);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  const fecha = new Date().toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  doc.text("Fecha: " + fecha, pageW - 8, contentY, { align: "right" });
  contentY += 6;

  // Caja de cliente (verde pastel redondeada)
  const ci = opts.clientInfo;
  if (ci && (ci.name || ci.email)) {
    doc.setFillColor(C_LIGHT[0], C_LIGHT[1], C_LIGHT[2]);
    doc.roundedRect(8, contentY, pageW - 16, 12, 2, 2, "F");

    doc.setTextColor(C_TEXT[0], C_TEXT[1], C_TEXT[2]);
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.text("Cliente:", 12, contentY + 7.5);

    doc.setFont("helvetica", "normal");
    let clientLine = ci.name || "";
    if (ci.email) { clientLine += "   " + ci.email; }
    if (ci.phone) { clientLine += "   " + ci.phone; }
    doc.text(clientLine, 32, contentY + 7.5);
    contentY += 16;
  }

  // Nota libre
  if (opts.note) {
    doc.setTextColor(C_GRAY[0], C_GRAY[1], C_GRAY[2]);
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "italic");
    doc.text("Nota: " + opts.note, 8, contentY + 4);
    contentY += 9;
  }

  contentY += 3;
  return { doc, contentY };
}

// ─── addTable ───────────────────────────────────────────────────────────────

function addTable(
  doc: jsPDF,
  columns: PdfTableColumn[],
  rows: any[],
  startY: number,
  optionsOrTotalsRowIndex?: number | { totalsRowIndex?: number; didDrawCell?: (data: any) => void; minCellHeight?: number }
): number {
  let totalsRowIndex: number | undefined = undefined;
  let didDrawCellFn: ((data: any) => void) | undefined = undefined;
  let minCellH: number | undefined = undefined;

  if (typeof optionsOrTotalsRowIndex === 'number') {
    totalsRowIndex = optionsOrTotalsRowIndex;
  } else if (optionsOrTotalsRowIndex) {
    totalsRowIndex = optionsOrTotalsRowIndex.totalsRowIndex;
    didDrawCellFn = optionsOrTotalsRowIndex.didDrawCell;
    minCellH = optionsOrTotalsRowIndex.minCellHeight;
  }

  const columnStyles: any = {};
  columns.forEach(function(col, i) {
    const style: any = {};
    if (col.width) { style.cellWidth = col.width; }
    if (col.align) { style.halign = col.align; }
    if (col.width || col.align) { columnStyles[i] = style; }
  });

  const opts: any = {
    startY,
    head: [columns.map(function(c) { return c.header; })],
    body: rows,
    styles: {
      fontSize: 8.5,
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
      textColor: [C_TEXT[0], C_TEXT[1], C_TEXT[2]],
    },
    headStyles: {
      fillColor: [C_PRIMARY[0], C_PRIMARY[1], C_PRIMARY[2]],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [248, 248, 248],
    },
    bodyStyles: {
      minCellHeight: minCellH ? minCellH : undefined,
    },
    columnStyles: columnStyles,
    didDrawCell: function(data: any) {
      if (didDrawCellFn) {
        didDrawCellFn(data);
      }
    },
    didParseCell: function(data: any) {
      if (totalsRowIndex !== undefined && data.row.index === totalsRowIndex) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [C_LIGHT[0], C_LIGHT[1], C_LIGHT[2]];
        data.cell.styles.textColor = [C_DARK[0],  C_DARK[1],  C_DARK[2]];
      }
    },
    margin: { left: 8, right: 8 },
  };

  autoTable(doc, opts);

  return (doc as any).lastAutoTable ? (doc as any).lastAutoTable.finalY : startY + 10;
}

// ─── Pie de página ──────────────────────────────────────────────────────────

function _addFooter(doc: jsPDF) {
  const pageCount = doc.getNumberOfPages();
  const pageW     = doc.internal.pageSize.getWidth();
  const pageH     = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(C_PRIMARY[0], C_PRIMARY[1], C_PRIMARY[2]);
    doc.setLineWidth(0.5);
    doc.line(8, pageH - 12, pageW - 8, pageH - 12);

    doc.setTextColor(C_GRAY[0], C_GRAY[1], C_GRAY[2]);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(BRAND_NAME + " — " + BRAND_SUBTITLE, 8, pageH - 7);
    doc.text("Pág. " + i + " de " + pageCount, pageW - 8, pageH - 7, { align: "right" });
  }
}

// ─── save ───────────────────────────────────────────────────────────────────

function save(doc: jsPDF, filename: string) {
  _addFooter(doc);
  const name = filename.endsWith(".pdf") ? filename : filename + ".pdf";
  doc.save(name);
}

// ─── Export ─────────────────────────────────────────────────────────────────

export const PdfCorporateService = {
  createDoc,
  addTable,
  save,
};
